import { z } from 'zod/v4';

export const ToggleAdminQuestionnaireTemplateParamsSchema = z.object({
  id: z.string().min(1),
});

export type ToggleAdminQuestionnaireTemplateParams = z.infer<
  typeof ToggleAdminQuestionnaireTemplateParamsSchema
>;

/**
 * Body of the admin template activation switch — the only writable field is the
 * `is_active` flag.
 *
 * `isActive` is a number, not a boolean, because the column is an integer and
 * that is what the API has always surfaced: `CreatedQuestionnaireSchema` reads it
 * back as `z.number()`, and the switch in the admin templates table sends
 * `Number(checked)`. Modelling it as `z.boolean()` here would reject every
 * request the existing caller makes, since zod does not coerce.
 *
 * One deliberate departure from the legacy AJV body
 * (`{name?: string(1..255), isActive?: number}`): **`isActive` is required and
 * narrowed to `0 | 1`.** Legacy declared it optional with no bounds, so
 * `PATCH {}` bound `Number(undefined)` — i.e. `NaN` — to an `integer` column and
 * answered 500, and `{isActive: 7}` happily stored 7 in a flag column. Both are
 * now 400s. `0 | 1` rather than `z.number().int()` because a flag has exactly two
 * states and no caller has ever sent anything else.
 *
 * **`name` is dropped from the schema but not rejected.** Legacy accepted it and
 * then threw it away — the service destructured only `{isActive}` and the UPDATE
 * had no `name =` assignment, so a rename through this endpoint silently did
 * nothing. It is not carried forward, since renaming goes through
 * `PUT /admin/questionnaires/templates/:id`. A caller that still sends it gets
 * the same 204-with-no-rename it always did: `z.object` strips unknown keys
 * rather than erroring, which is exactly what AJV's `removeAdditional` did to
 * the legacy body's `additionalProperties: false`. Deliberate parity, and the
 * same choice the sibling `post.ts` documents for the `status` its caller sends.
 */
export const ToggleAdminQuestionnaireTemplateBodySchema = z.object({
  isActive: z.union([z.literal(0), z.literal(1)]),
});

export type ToggleAdminQuestionnaireTemplateBody = z.infer<
  typeof ToggleAdminQuestionnaireTemplateBodySchema
>;
